import { LitElement, html, css } from 'lit';
import { store } from '../lib/store.js';
import { browserUrl } from '../lib/build-paths.js';

/**
 * Webshop-style product list for the CMS `products` module.
 * Client-side search and pagination; price and badge are optional per item.
 * UI strings come from site settings `interface` (products_* keys).
 * Theme via CSS vars and the exposed parts (card, image, title, desc, price,
 * badge, link, search, pager, pager-button).
 */
export class SiteProductList extends LitElement {
  static properties = {
    items: { type: Array },
    pageSize: { type: Number },
    columns: { type: Number },
    searchPlaceholder: { type: String },
    _query: { state: true },
    _page: { state: true },
    _selected: { state: true },
    _galleryIndex: { state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
    .toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-4, 1rem);
      margin-bottom: var(--space-6, 1.5rem);
      flex-wrap: wrap;
    }
    .search {
      flex: 1;
      min-width: 220px;
      max-width: 420px;
      position: relative;
    }
    .search input {
      width: 100%;
      min-height: 2.75rem;
      box-sizing: border-box;
      padding: var(--space-3, 0.75rem) var(--space-4, 1rem) var(--space-3, 0.75rem) 2.5rem;
      border: 1px solid var(--color-border, #e4e7eb);
      border-radius: 999px;
      font-size: var(--text-sm, 0.875rem);
      font-family: inherit;
      color: var(--color-text-primary, #16202f);
      background: var(--color-bg-card, #fff);
      outline: none;
      transition: border-color 150ms ease;
    }
    .search input:focus {
      border-color: var(--color-accent, #e00020);
    }
    .search svg {
      position: absolute;
      left: 0.9rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--color-text-muted, #8e96a3);
      pointer-events: none;
    }
    .count {
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-muted, #8e96a3);
      white-space: nowrap;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(var(--columns, 3), minmax(0, 1fr));
      gap: var(--space-6, 1.5rem);
    }
    .card {
      background: var(--color-bg-card, #fff);
      border: 1px solid var(--color-border, #e4e7eb);
      border-radius: var(--border-radius-xl, 1rem);
      padding: var(--space-6, 1.5rem);
      display: flex;
      flex-direction: column;
      position: relative;
      transition: box-shadow 250ms ease;
    }
    .card:hover {
      box-shadow: 0 8px 30px rgba(22, 32, 47, .06);
    }
    .card-open { display: flex; flex: 1; flex-direction: column; width: 100%; padding: 0; border: 0; color: inherit; background: transparent; font: inherit; text-align: left; cursor: pointer; }
    .badge {
      position: absolute;
      top: var(--space-4, 1rem);
      left: var(--space-4, 1rem);
      z-index: 2;
      background: var(--color-accent, #e00020);
      color: #fff;
      font-size: var(--text-xs, 0.75rem);
      font-weight: 600;
      padding: 0.15rem 0.6rem;
      border-radius: 999px;
    }
    .thumb {
      width: 100%;
      height: 140px;
      border-radius: var(--border-radius, 0.5rem);
      overflow: hidden;
      margin-bottom: var(--space-4, 1rem);
      background: linear-gradient(135deg, #dfe6ee 0%, #c3cfdb 50%, #aebdcd 100%);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .thumb img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }
    .thumb span {
      font-size: var(--text-xs, 0.75rem);
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #4a5a6d;
      opacity: 0.75;
      padding: 0 0.5rem;
      text-align: center;
    }
    .title {
      font-size: var(--text-base, 1rem);
      font-weight: 700;
      color: var(--color-text-primary, #16202f);
      margin: 0 0 var(--space-1, 0.25rem);
    }
    .desc {
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-muted, #8e96a3);
      line-height: 1.6;
      margin: 0 0 var(--space-3, 0.75rem);
      white-space: pre-line;
    }
    .variants {
      display: grid;
      gap: 0.35rem;
      margin: 0 0 var(--space-3, 0.75rem);
      padding: 0;
      list-style: none;
    }
    .variants li {
      font-size: var(--text-xs, 0.75rem);
      color: var(--color-text-muted, #6f7886);
      line-height: 1.45;
    }
    .variants strong { color: var(--color-text-primary, #16202f); }
    .footer {
      margin-top: auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-3, 0.75rem);
    }
    .price {
      font-size: var(--text-base, 1rem);
      font-weight: 700;
      color: var(--color-text-primary, #16202f);
    }
    .link {
      font-size: var(--text-sm, 0.875rem);
      font-weight: 600;
      color: var(--color-accent, #e00020);
      text-decoration: none;
      white-space: nowrap;
    }
    .link:hover {
      color: var(--color-accent-hover, #c2001c);
    }
    .empty {
      padding: var(--space-10, 2.5rem);
      text-align: center;
      color: var(--color-text-muted, #8e96a3);
      font-size: var(--text-sm, 0.875rem);
    }
    .pager {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-2, 0.5rem);
      margin-top: var(--space-8, 2rem);
      flex-wrap: wrap;
    }
    .pager-button {
      min-width: 2.75rem;
      height: 2.75rem;
      padding: 0 0.6rem;
      border-radius: 999px;
      border: 1px solid var(--color-border, #e4e7eb);
      background: var(--color-bg-card, #fff);
      color: var(--color-text-secondary, #596170);
      font-size: var(--text-sm, 0.875rem);
      font-weight: 600;
      cursor: pointer;
      transition: all 150ms ease;
    }
    .pager-button:hover:not(:disabled) {
      border-color: var(--color-accent, #e00020);
      color: var(--color-accent, #e00020);
    }
    .pager-button--active {
      background: var(--color-accent, #e00020);
      border-color: var(--color-accent, #e00020);
      color: #fff;
    }
    .pager-button:disabled {
      opacity: 0.4;
      cursor: default;
    }
    dialog { width: min(940px,calc(100vw - 2rem)); max-height: calc(100vh - 2rem); padding: 0; border: 0; border-radius: var(--border-radius-xl,1rem); color: var(--color-text-primary,#16202f); background: var(--color-bg-card,#fff); box-shadow: 0 30px 90px rgba(0,0,0,.28); }
    dialog::backdrop { background: rgba(15,23,42,.68); backdrop-filter: blur(3px); }
    .detail { display: grid; grid-template-columns: minmax(0,1.2fr) minmax(280px,.8fr); min-height: 440px; }
    .detail-media { display: grid; grid-template-rows: minmax(300px,1fr) auto; gap: .75rem; padding: 1.25rem; background: #f2f4f7; }
    .detail-main { display: grid; place-items: center; min-height: 300px; }
    .detail-main img { width: 100%; max-height: 58vh; object-fit: contain; }
    .detail-thumbs { display: flex; gap: .5rem; overflow-x: auto; }
    .detail-thumbs button { flex: 0 0 70px; height: 58px; padding: 3px; border: 2px solid transparent; border-radius: .5rem; background: #fff; cursor: pointer; }
    .detail-thumbs button[aria-current="true"] { border-color: var(--color-accent,#e00020); }
    .detail-thumbs img { width: 100%; height: 100%; object-fit: contain; }
    .detail-copy { position: relative; padding: clamp(1.5rem,4vw,2.5rem); overflow: auto; }
    .detail-close { position: absolute; top: .8rem; right: .8rem; width: 2.75rem; height: 2.75rem; border: 1px solid var(--color-border,#e4e7eb); border-radius: 999px; background: #fff; font-size: 1.25rem; cursor: pointer; }
    .detail-copy h2 { margin: 1.5rem 0 .5rem; font-size: clamp(1.6rem,3vw,2.3rem); }
    .detail-price { display: block; margin: 1rem 0; font-size: 1.2rem; font-weight: 800; }
    .detail-link { display: inline-flex; margin-top: 1rem; padding: .75rem 1rem; border-radius: 999px; color: #fff; background: var(--color-accent,#e00020); font-weight: 700; text-decoration: none; }
    @media (max-width: 900px) {
      .grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
    @media (max-width: 560px) {
      .grid {
        grid-template-columns: 1fr;
      }
      .detail { grid-template-columns: 1fr; }
      .detail-media { grid-template-rows: minmax(220px,35vh) auto; }
    }
  `;

  constructor() {
    super();
    this.items = [];
    this.pageSize = 12;
    this.columns = 3;
    this.searchPlaceholder = '';
    this._query = '';
    this._page = 1;
    this._selected = null;
    this._galleryIndex = 0;
    this._hashProduct = '';
    this._onHashChange = () => this._openHashProduct();
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('hashchange', this._onHashChange);
    this.updateComplete.then(() => this._openHashProduct());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this._onHashChange);
  }

  updated(changed) {
    if (changed.has('items')) this._openHashProduct();
  }

  _strings() {
    const iface = store.getSiteSettings()?.interface || {};
    return {
      search: this.searchPlaceholder || iface.products_search_placeholder || 'Search products…',
      count: (n) => (iface.products_count_text || '{count} products').replace('{count}', n),
      empty: iface.products_empty_text || 'No products found.',
      previous: iface.products_previous_label || 'Previous page',
      next: iface.products_next_label || 'Next page',
      link: iface.default_link_text || 'Learn more',
    };
  }

  _filtered() {
    const q = this._query.trim().toLowerCase();
    if (!q) return this.items || [];
    return (this.items || []).filter((item) =>
      [item.title, item.description, item.price, item.badge, ...(item.variants || []).flatMap((variant) => [variant.name, variant.description])]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q))
    );
  }

  _onSearch(event) {
    this._query = event.target.value;
    this._page = 1;
  }

  _goTo(page) {
    this._page = page;
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _pageNumbers(pageCount) {
    const current = this._page;
    const pages = new Set([1, pageCount, current - 1, current, current + 1]);
    return [...pages].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b)
      .reduce((acc, p, i, arr) => {
        if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
        acc.push(p);
        return acc;
      }, []);
  }

  _openProduct(item) {
    this._selected = item;
    this._galleryIndex = 0;
    this.updateComplete.then(() => this.renderRoot.querySelector('dialog')?.showModal());
  }

  _openHashProduct() {
    let id = '';
    try { id = decodeURIComponent(window.location.hash.replace(/^#/, '')); } catch { return; }
    if (!/^prd_[a-zA-Z0-9_-]{12,80}$/.test(id) || id === this._hashProduct) return;
    const index = (this.items || []).findIndex((item) => item.id === id);
    if (index < 0) return;
    this._hashProduct = id;
    this._query = '';
    this._page = Math.floor(index / Math.max(1, this.pageSize || 12)) + 1;
    this._openProduct(this.items[index]);
  }

  _closeProduct() {
    const selectedId = this._selected?.id || '';
    this.renderRoot.querySelector('dialog')?.close();
    this._selected = null;
    if (selectedId && window.location.hash === `#${encodeURIComponent(selectedId)}`) {
      history.replaceState(history.state, '', `${location.pathname}${location.search}`);
    }
    this._hashProduct = '';
    this.updateComplete.then(() => this.renderRoot.querySelector(`[data-product-id="${selectedId}"] .card-open`)?.focus());
  }

  render() {
    const s = this._strings();
    const filtered = this._filtered();
    const size = Math.max(1, this.pageSize || 12);
    const pageCount = Math.max(1, Math.ceil(filtered.length / size));
    const page = Math.min(this._page, pageCount);
    const visible = filtered.slice((page - 1) * size, page * size);

    return html`
      <div class="toolbar" part="toolbar">
        <div class="search" part="search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input type="search" .value=${this._query} @input=${this._onSearch}
                 placeholder=${s.search}
                 aria-label=${s.search}>
        </div>
        <span class="count" part="count">${s.count(filtered.length)}</span>
      </div>

      ${visible.length === 0 ? html`
        <div class="empty" part="empty">${s.empty}</div>
      ` : html`
        <div class="grid" part="grid" style="--columns:${this.columns || 3}">
          ${visible.map((item) => html`
            <article class="card" part="card" id=${item.id || ''} data-product-id=${item.id || ''}>
              ${item.badge ? html`<span class="badge" part="badge">${item.badge}</span>` : ''}
              <button class="card-open" @click=${() => this._openProduct(item)} aria-label=${`View ${item.title || 'product'} details`}>
              <div class="thumb" part="image">
                ${item.image
                  ? html`<img src=${browserUrl(item.image)} alt=${item.title || ''} loading="lazy">`
                  : html`<span>${item.title || 'Product'}</span>`}
              </div>
              <h3 class="title" part="title">${item.title}</h3>
              ${item.description ? html`<p class="desc" part="desc">${item.description}</p>` : ''}
              ${item.variants?.length ? html`<ul class="variants" part="variants">${item.variants.map((variant) => html`<li><strong>${variant.name}</strong>${variant.name && variant.description ? ': ' : ''}${variant.description}</li>`)}</ul>` : ''}
              <div class="footer">
                ${item.price ? html`<span class="price" part="price">${item.price}</span>` : html`<span></span>`}
              </div>
              </button>
              ${item.link ? html`<a class="link" part="link" href=${browserUrl(item.link)}>${item.linkText || s.link} →</a>` : ''}
            </article>
          `)}
        </div>

        ${pageCount > 1 ? html`
          <nav class="pager" part="pager" aria-label="Paginering">
            <button class="pager-button" part="pager-button" ?disabled=${page === 1}
                    @click=${() => this._goTo(page - 1)} aria-label=${s.previous}>←</button>
            ${this._pageNumbers(pageCount).map((p) => p === '…'
              ? html`<span class="pager-button" style="border:0;background:none;cursor:default">…</span>`
              : html`<button class="pager-button ${p === page ? 'pager-button--active' : ''}" part="pager-button"
                            @click=${() => this._goTo(p)}>${p}</button>`)}
            <button class="pager-button" part="pager-button" ?disabled=${page === pageCount}
                    @click=${() => this._goTo(page + 1)} aria-label=${s.next}>→</button>
          </nav>
        ` : ''}
      `}
      ${this._selected ? html`<dialog @cancel=${() => this._closeProduct()} @click=${event => { if (event.target === event.currentTarget) this._closeProduct(); }} aria-labelledby="product-detail-title"><div class="detail"><section class="detail-media">${[this._selected.image,...(this._selected.gallery||[])].filter(Boolean).length?html`<div class="detail-main"><img src=${browserUrl([this._selected.image,...(this._selected.gallery||[])].filter(Boolean)[this._galleryIndex]||this._selected.image)} alt=${this._selected.title||''}></div><div class="detail-thumbs">${[this._selected.image,...(this._selected.gallery||[])].filter(Boolean).map((url,index)=>html`<button aria-current=${this._galleryIndex===index} aria-label=${`Show image ${index+1}`} @click=${()=>this._galleryIndex=index}><img src=${browserUrl(url)} alt=""></button>`)}</div>`:html`<div class="detail-main">No product image</div>`}</section><section class="detail-copy"><button class="detail-close" aria-label="Close product details" @click=${()=>this._closeProduct()}>×</button>${this._selected.badge?html`<span class="badge">${this._selected.badge}</span>`:''}<h2 id="product-detail-title">${this._selected.title}</h2>${this._selected.description?html`<p class="desc">${this._selected.description}</p>`:''}${this._selected.price?html`<span class="detail-price">${this._selected.price}</span>`:''}${this._selected.variants?.length?html`<h3>Variants and sizes</h3><ul class="variants">${this._selected.variants.map(variant=>html`<li><strong>${variant.name}</strong>${variant.name&&variant.description?': ':''}${variant.description}</li>`)}</ul>`:''}${this._selected.link?html`<a class="detail-link" href=${browserUrl(this._selected.link)}>${this._selected.linkText||s.link} →</a>`:''}</section></div></dialog>`:''}
    `;
  }
}

customElements.define('site-product-list', SiteProductList);
